import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ReactEcharts from 'echarts-for-react';
import Select from 'react-select';
import StandardPerformanceTable from 'wt-react/dist/components/StandardPerformance/StandardPerformanceTable.js';
import DetailsTable from 'wt-react/dist/components/StandardPerformance/DetailsTable.js';
import ReactTooltip from 'react-tooltip';
import {
  setEditMode,
  deleteClient,
  setEditClient,
} from '../../actions/advisor';
import {
  BS_TEST,
  LA_TEST,
  RR_TEST,
  LF_TEST,
} from '../../constants.js';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRibbon, faCertificate, faEdit, faRocket, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import s from './PreferredModels.css';
import './tooltip.css';

library.add(faRibbon, faCertificate, faEdit, faRocket, faInfoCircle);

const customStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 0,
    boxShadow: 'none',
    height: '40px',
    fontSize: 16,
  }),
  option: (provided) => ({
    ...provided,
    color: '#050139',
    fontSize: 14,
    borderRadius: 0,
    backgroundColor: 'white',
    ':hover': {
      backgroundColor: '#5060F6',
      color: 'white',
    },
  }),
  menu: base => ({
    ...base,
    borderRadius: 0,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = 'opacity 100ms';
    return { ...provided, opacity, transition, color: '#050139' };
  },
  dropdownIndicator: (base) => ({
    ...base,
    color: '#5060F6',
  }),
};


class PreferredModels extends PureComponent {

  constructor(props) {
    super(props);
    this.state = {
      viewAllModels: false,
      selectedMetric: { value: 'description', label: 'Model Overview' },
    };
  }

  getLastTestResult(test, results) {
    if (results) {
      const sortedResults = results.filter((result) => result.name === test);
      if (sortedResults.length > 0) {
        return sortedResults[sortedResults.length - 1];
      }
      return null;
    } else {
      return null;
    }
  }

  setViewAllModels = () => {
    this.setState({ viewAllModels: true });
  }

  selectMetric(metric) {
    this.setState({ selectedMetric: metric });
  };

  handleSelectedMetricChange = (metric) => {
    this.setState({ selectedMetric: metric });
  };

  formatPercent(number) {
    return number !== null ? parseFloat(number * 100).toFixed(1) : null;
  }

  renderCompositionRows(clientModel, wtModel) {
    const chld = clientModel.portfolioAnalysis.holdings[0];
    const whld = wtModel.modelAnalysis.holdings[0];
    const count = Math.max(chld.tickers.length, whld.tickers.length);

    return Array.apply(null, Array(count)).map((row, idx) => {
      return (
        <tr>
          { idx < chld.tickers.length && chld.names.length &&
            <td>
              <div className={s.compName}>{chld.tickers[idx]}</div>
              <div className={s.compDesc}>{chld.names[idx]}</div>
            </td>
          }
          { idx < chld.tickers.length && chld.names.length &&
            <td className={s.compClient}>
              {this.formatPercent(chld.weights[idx])}<span style={{ fontSize: '14px' }}>%</span>
            </td>
          }
          { idx >= chld.tickers.length &&
            <td>
              <div className={s.compName}>&nbsp;</div>
              <div className={s.compDesc}>&nbsp;</div>
            </td>
          }
          { idx >= chld.tickers.length &&
            <td className={s.compClient}>&nbsp;</td>
          }
          { idx < whld.tickers.length && whld.names.length &&
            <td>
              <div className={s.compName}>{whld.tickers[idx]}</div>
              <div className={s.compDesc}>{whld.names[idx]}</div>
            </td>
          }
          { idx < whld.tickers.length && whld.names.length &&
            <td className={s.compWT}>
              {this.formatPercent(whld.weights[idx])}<span style={{ fontSize: '14px' }}>%</span>
            </td>
          }
          { idx >= whld.tickers.length &&
            <td>
              <div className={s.compName}>&nbsp;</div>
              <div className={s.compDesc}>&nbsp;</div>
            </td>
          }
          { idx >= whld.tickers.length &&
            <td className={s.compWT}>&nbsp;</td>
          }
        </tr>
      );
    });
  }

  renderSelectedModels() {
    const {
      client,
    } = this.props;

    const {
      selectedMetric,
      viewAllModels,
    } = this.state;

    // get the client port
    const clientModel = client.fullTermModels.filter((x) => {
      return x.name === 'Client Portfolio';
    })[0];

    // get the wt models
    const wtModels = client.fullTermModels.filter((x) => {
      return x.name !== 'Client Portfolio';
    });

    // calc pct better score then filter losers and sort winners
    let models = wtModels.map((mod) => {
      mod.pctBetter = (mod.scoreWgt - clientModel.scoreWgt) * 100; s
      mod.modelDescription = mod.textRiskTolerance;
      return mod;
    }).filter((mod) => {
      return mod.pctBetter > 0;
    }).sort((a, b) => {
      return b.pctBetter - a.pctBetter
    });

    // get the loss frequency result to see if we need to show an alternate model
    const lossResult = this.getLastTestResult(LF_TEST, client.results);
    let lossScore = 'low';
    if (lossResult) {
      lossScore = lossResult.score[0];
    }

    // set the preferred and alternat models reccs
    if (models.length > 0) {
      models = models.slice(0, 9);
      if (lossScore === 'low') {
        models[0].prefered = 'Suggested Model';
      } else {
        models[0].prefered = 'Suggested Long-Term Model';
        const vol = models[0].expectedVolatility;
        models = models.sort((a, b) => {
          return b.expectedVolatility - a.expectedVolatility
        });
        for (const model of models) {
          if (model.expectedVolatility < vol) {
            model.prefered = 'Alternate Short-Term Model';
            break;
          }
        }
      }
    }

    // sort and get our list to display
    models = models.sort((a, b) => {
      return b.pctBetter - a.pctBetter
    });

    if (!viewAllModels) {
      models = models.slice(0, 3);
    }

    return (
      models.map((mod, idx) => {
        // make sure we have analysis data
        if(!mod.modelAnalysis || !mod.portfolioAnalysis) {
          return null;
        }

        mod.modelAnalysis.name = 'WisdomTree Model/Client Portfolio Blend';
        mod.portfolioAnalysis.name = `${client.firstName}'s Portfolio`;
        const maxMonteCarloValue = Math.ceil((mod.returnsHigh[mod.returnsHigh.length - 1] * .04) / 10000) * 10000;
        const goalMonteCarlo = client.retireMonthlyIncome;
        const expAsof = new Date(mod.portfolioAnalysis.countryExposures[0].date).toLocaleDateString();

        const holdings = mod.modelAnalysis.holdings[0];
        const stdPerfModel = holdings.names.map((value, num) => {
          return (
            {
              ticker: holdings.tickers[num],
              name: value,
              stdPerformance: holdings.stdPerformance[num],
            }
          )
        });
        const modelDetailsData = holdings.names.map((value, num) => {
          return (
            {
              ticker: holdings.tickers[num],
              name: value,
              stdPerformance: [holdings.stdPerformance[num]],
              expenseNotes: holdings.expenseNotes[num],
              expenseRatio: holdings.expenseRatios[num],
              expenseRatioGross: holdings.expenseRatiosGross[num],
              inceptionDate: holdings.inceptionDates[num],
            }
          )
        });
        const holdingsPort = mod.portfolioAnalysis.holdings[0];
        const stdPerfPort = holdingsPort.names.map((value, num) => {
          return (
            {
              ticker: holdingsPort.tickers[num],
              name: value,
              stdPerformance: holdingsPort.stdPerformance[num],
            }
          )
        });
        const portDetailsData = holdingsPort.names.map((value, num) => {
          return (
            {
              ticker: holdings.tickers[num],
              name: value,
              stdPerformance: [holdings.stdPerformance[num]],
              expenseNotes: holdings.expenseNotes[num],
              expenseRatio: holdings.expenseRatios[num],
              expenseRatioGross: holdings.expenseRatiosGross[num],
              inceptionDate: holdings.inceptionDates[num],
            }
          )
        });

        const stdperfmodelOption = { value: 'stdperfport', label: 'Standardized Performance for Client Portfolio Constituents' };
        const stdperfportOption = { value: 'stdperfmodel', label: 'Standardized Performance for WisdomTree Model Constituents' };
        const detailsportOption = { value: 'portdetails', label: 'Details for Client Portfolio Constituents' };
        const detailsmodelOption = { value: 'modeldetails', label: 'Details for WisdomTree Model Constituents' };

        return (
          <div key={mod.name}  className={`row ${s.resultsRow}`}>
            <div className="col-md-12" style={{paddingLeft:'0px',paddingRight:'0px'}}>
            <div className='row'>
              <div className="col-md-12">
                <div className={s.resultsScore}>
                  <span style={{color:'#5060F6'}}>{idx + 1}.</span> {mod.name}
                </div>
              </div>
            </div>
            <div className={`row ${s.resultsRow2}`}>
              <div className="col-md-4" data-tip data-for={'mceScore'}>
                <div className={s.resultsGoalScore}>
                  {Math.round(mod.pctBetter)}%
                </div>
                <div className={s.resultsGoalScore2}>
                  BeFi Analysis Score Improvement
                </div>
              </div>
              <ReactTooltip id={'mceScore'} type='light' className='clienttip' effect="float">
                The percent improvement between the BeFi Analysis Score of the model to the BeFi Analysis Score of {client.firstName}'s portfolio.
              </ReactTooltip>
              <div className="col-md-3" data-tip data-for={'goal'}>
                <div className={s.resultsGoalScore}>
                  {Math.round(mod.metGoalPct * 100)}%
                </div>
                <div className={s.resultsGoalScore2}>
                  Chance to Meet Goal
                </div>

              </div>
              <ReactTooltip id={'goal'} type='light' className='clienttip' effect="float">
                The percent chance that the income goal simulations have to exceed the income goal set by {client.firstName}.
              </ReactTooltip>
              <div className="col-md-5" data-tip data-for={'prefered'}>
                {mod.prefered &&
                  <div className={s.preferedModel}>
                    {mod.prefered}&nbsp;&nbsp;&nbsp;<FontAwesomeIcon icon="ribbon" size="lg" style={{fontSize:'24px', color:'#37BAC0'}}/>
                  </div>
                }
              </div>
              <ReactTooltip id={'prefered'} type='light' className='clienttip' effect="float">
                Based on {client.firstName}'s assessment, the ideal model would be most suitable long term,
                while the alternate model may be a less risky option for the next few years.
              </ReactTooltip>
            </div>
            <div className='row'>
              <div className="col-md-12">
                <div className={selectedMetric.value === 'none' ? s.dropdownRow : s.dropdownRow2}>
                  <Select
                    options={[
                      { value: 'description', label: 'Model Overview' },
                      { value: 'montecarlo', label: 'Hypothetical Yearly Retirement Income' },
                      { value: 'keymetrics', label: 'Key Metrics' },
                      { value: 'composition', label: 'Composition' },
                      { value: 'sectors', label: 'Sector Exposure' },
                      { value: 'country', label: 'Country Exposure' },
                      { value: 'marketcap', label: 'Market Cap Exposure' },
                      { value: 'stdperf', label: 'Month End Performance' },
                      { value: 'stdperfport', label: 'Standardized Performance for Client Portfolio Constituents' },
                      { value: 'stdperfmodel', label: 'Standardized Performance for WisdomTree Model Constituents' },
                      { value: 'portdetails', label: 'Details for Client Portfolio Constituents' },
                      { value: 'modeldetails', label: 'Details for WisdomTree Model Constituents' },
                    ]}
                    styles={customStyles}
                    value={selectedMetric}
                    onChange={this.handleSelectedMetricChange}
                  />
                </div>
              </div>
            </div>
            {selectedMetric.value === 'description' &&
              <React.Fragment>
              <div className='row'>
                <div className="col-md-12">
                  <div className={s.resultsDescription}>{mod.modelDescription}</div>
                </div>
              </div>
              <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                <div className="col-md-12">
                  <div className={s.disclosures}>
                  There is no guarantee that any investment strategy will achieve its objectives, generate positive returns, or avoid losses.
                  </div>
                </div>
              </div>
              </React.Fragment>
            }
            {selectedMetric.value === 'montecarlo' &&
              <React.Fragment>
                <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                  <div className="col-md-6">
                    <div className={s.simulationHeader} style={{ color: '#37BAC0' }}>{client.firstName}'s Portfolio</div>
                    <div className={s.simulationSubheader}>
                      Hypothetical Returns: {this.formatPercent(clientModel.expectedReturns)}%
                            &nbsp;&nbsp;Hypothetical Volatility: {this.formatPercent(clientModel.expectedVolatility)}%
                          </div>
                    <ReactEcharts
                      option={this.getMonteCarloChart(clientModel, maxMonteCarloValue, goalMonteCarlo, '#37BAC0')}
                      theme='default'
                      style={{ height: 300 }}
                    />
                  </div>
                  <div className="col-md-6">
                    <div className={s.simulationHeader} style={{ color: '#5060F6' }}>WisdomTree Model/Client Portfolio Blend</div>
                    <div className={s.simulationSubheader}>
                      Hypothetical Returns: {this.formatPercent(mod.expectedReturns)}%
                            &nbsp;&nbsp;Hypothetical Volatility: {this.formatPercent(mod.expectedVolatility)}%
                          </div>
                    <ReactEcharts
                      option={this.getMonteCarloChart(mod, maxMonteCarloValue, goalMonteCarlo, '#5060F6')}
                      theme='default'
                      style={{ height: 300 }}
                    />
                  </div>
                </div>
                <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                  <div className="col-md-12">
                    <div className={s.disclosures}>
                      The Hypothetical Yearly Retirement Income chart uses Monte Carlo simulations to illustrate the probabilities
                      of achieving various portfolio balances starting with the portfolio value in the current year and moving in
                      1 year increments through the end of the analysis projections illustrating the portfolio’s values at various
                      probabilities ranging from a 5% to 95% probability.
                      A Monte Carlo analysis models the probability of different investment outcomes and does not imply or guarantee
                      future returns. Results may vary with each use and over time.
                      The chart calculates a 10,000-iteration Monte Carlo analysis that produces probability distributions over time,
                      accounting for contributions and retirement-stage withdrawals from a portfolio.
                      The inputs used in the Monte Carlo calculations are based on current portfolio balances and holdings, as well
                      as the expected return and expected volatility analytics, both of which are calculated using a 16 factor-based approach.
                      Expected returns are calculated for a portfolio by analyzing the sensitivity of the portfolio to each
                      individual factor, the portfolio sensitivity is then applied to estimates of long-run future returns for each factor.
                      The default estimates of long-term future returns for the individual factors (Capital markets expected
                      return assumptions) are calculated by FinMason and are determined by using historial 10-year returns for each factor.
                      The expected volatility is the long-run estimate of the future volatility of a portfolio based upon
                      its exposure to the 16 factors and each individual factor's historical volatility (Capital markets
                      expected volatilities assumptions) over the last 10 years.
                            <strong>More information regarding the various factors is available in the 'Global multi-asset factor set'
                            section <a href="https://www.wisdomtree.com/advisor-solutions/digital-portfolio-developer/stress-test-methodology" target="_new">here</a>.</strong>
                            The models may utilize market proxies, individual fund returns, or security level returns where relevant data is available.
                            The hypothetical expected case, bad case, and good case are for illustrative purposes only, and not a guarantee of future returns.
                            Factors which are not statistically relevant in the sensitivity analysis of a particular portfolio are not included within the
                            simulation.<br />
                      <strong>IMPORTANT: The projections or other information generated by this chart regarding the likelihood of various investment
                            outcomes are hypothetical in nature, do not reflect actual investment results and are not guarantees of future results.</strong>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            }
            {selectedMetric.value === 'keymetrics' &&
              <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                <div className="col-md-12">
                  <table className={`table ${s.metricsTable}`}>
                    <thead>
                      <tr style={{ backgroundColor: '#FFFFFF' }}>
                        <th className={s.metricsHeader}>
                          Common Period: {new Date(mod.modelAnalysis.commonPeriodStart).toLocaleDateString()}
                                to {new Date(mod.modelAnalysis.commonPeriodEnd).toLocaleDateString()}
                        </th>
                        <th className={s.metricsClientHeader}>{client.firstName}'s Portfolio</th>
                        <th className={s.metricsWTHeader}>WisdomTree Model/Client Portfolio Blend</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <div className={s.metricName}>Income</div>
                          <div className={s.metricDesc}>Trailing 12M Dividend Yields</div>
                        </td>
                        <td className={s.metricClient}>{this.formatPercent(mod.portfolioAnalysis.statistics.dividendYield)}<span style={{ fontSize: '14px' }}>%</span></td>
                        <td className={s.metricWT}>{this.formatPercent(mod.modelAnalysis.statistics.dividendYield)}<span style={{ fontSize: '14px' }}>%</span></td>
                      </tr>
                      <tr>
                        <td>
                          <div className={s.metricName}>Performance</div>
                          <div className={s.metricDesc}>Avg Annual Returns over Common Period</div>
                        </td>
                        <td className={s.metricClient}>
                          {this.formatPercent(mod.portfolioAnalysis.statistics.avgAnnualPerformance)}<span style={{ fontSize: '14px' }}>%</span>
                        </td>
                        <td className={s.metricWT}>{this.formatPercent(mod.modelAnalysis.statistics.avgAnnualPerformance)}<span style={{ fontSize: '14px' }}>%</span></td>
                      </tr>
                      <tr>
                        <td>
                          <div className={s.metricName}>Risk</div>
                          <div className={s.metricDesc}>Avg Annual Volatility over Common Period</div>
                        </td>
                        <td className={s.metricClient}>
                          {this.formatPercent(mod.portfolioAnalysis.statistics.avgAnnualVolatility)}<span style={{ fontSize: '14px' }}>%</span>
                        </td>
                        <td className={s.metricWT}>{this.formatPercent(mod.modelAnalysis.statistics.avgAnnualVolatility)}<span style={{ fontSize: '14px' }}>%</span></td>
                      </tr>
                      <tr>
                        <td>
                          <div className={s.metricName}>Risk-Adj. Returns</div>
                          <div className={s.metricDesc}>Avg Annual Sharpe Ratio over Common Period</div>
                        </td>
                        <td className={s.metricClient}>{(mod.portfolioAnalysis.statistics.sharpeRatioMean * 100).toFixed(2)}</td>
                        <td className={s.metricWT}>{(mod.modelAnalysis.statistics.sharpeRatioMean * 100).toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td>
                          <div className={s.metricName}>Fees</div>
                          <div className={s.metricDesc}>Net Expense Ratio</div>
                        </td>
                        <td className={s.metricClient}>
                          {(mod.portfolioAnalysis.statistics.aggregateExpenseRatio * 10000).toFixed(0)}<span style={{ fontSize: '14px' }}>bps</span>
                        </td>
                        <td className={s.metricWT}>{(mod.modelAnalysis.statistics.aggregateExpenseRatio * 10000).toFixed(0)}<span style={{ fontSize: '14px' }}>bps</span></td>
                      </tr>
                    </tbody>
                  </table>
                  <div className={s.disclosures}>
                    <a style={{color:'#37BAC0',fontWeight:'700'}} onClick={() => this.selectMetric(detailsportOption)}>
                      View Details for Client Portfolio Constituents
                    </a><br />
                    <a style={{color:'#5060F6',fontWeight:'700'}} onClick={() => this.selectMetric(detailsmodelOption)}>
                      View Details for Wisdomtree Model Constituents
                    </a><br />
                    Performance is historical and does not guarantee future results. Current performance may be lower or higher
                    than quoted. Investment returns and principal value of an investment will fluctuate so that an investor's shares,
                    when redeemed, may be worth more or less than their original cost.The most recent month end performance for underlying
                    funds can be found on the individual funds' website. For this and other important information please click on
                    the View Details links above. In addition, details regarding the underlying funds 30-Day SEC Yield can be
                    found by clicking the View Details links above.
                    </div>
                </div>
              </div>
            }
            {selectedMetric.value === 'composition' &&
              <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                <div className="col-md-12">
                  <table className={`table ${s.metricsTable}`}>
                    <thead>
                      <tr style={{ backgroundColor: '#FFFFFF' }}>
                        <th colSpan="2" className={s.compClientHeader}>
                          {client.firstName}'s Portfolio<br />
                          <span style={{ fontSize: '14px', fontStyle: 'italic', color: '#5b6770', fontWeight: '400' }}>
                            {Math.round(mod.portfolioAnalysis.assetClassExposures[0].values[0] * 100)}%
                                  Equity | {Math.round(mod.portfolioAnalysis.assetClassExposures[0].values[1] * 100)}% Fixed Income
                                </span>
                        </th>
                        <th colSpan="2" className={s.compWTHeader}>
                          WisdomTree Model/Client Portfolio Blend<br />
                          <span style={{ fontSize: '14px', fontStyle: 'italic', color: '#5b6770', fontWeight: '400' }}>
                            {Math.round(mod.modelAnalysis.assetClassExposures[0].values[1] * 100)}%
                                  Equity | {Math.round(mod.modelAnalysis.assetClassExposures[0].values[2] * 100)}% Fixed Income
                                </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.renderCompositionRows(clientModel, mod)}
                    </tbody>
                  </table>
                  <div className={s.disclosures}>
                    As of {expAsof}.
                        </div>
                </div>
              </div>
            }
            {selectedMetric.value === 'sectors' &&
              <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                <div className="col-md-12">
                  <ReactEcharts
                    option={this.getSectorChart(clientModel, mod, client.firstName)}
                    theme='default'
                    style={{ height: 340 }}
                  />
                  <div className={s.disclosures}>
                    As of {expAsof}. Sectors and industries are defined by Global Industry Classification
                          Standards (GICS®). The portfolio is subject to underlying risks of the
                          Sector exposures given. For a detailed discussion of these risks of the underlying
                          Sector exposures please see the <a href="https://www.wisdomtree.com/advisor-solutions/digital-portfolio-developer/important-information#important-risk-information" target="_blank">Important Risk Information</a>.
                  </div>
                </div>
              </div>
            }
            {selectedMetric.value === 'country' &&
              <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                <div className="col-md-12">
                  <ReactEcharts
                    option={this.getCountryChart(clientModel, mod, client.firstName)}
                    theme='default'
                    style={{ height: (this.getUniqueCountries([mod.portfolioAnalysis, mod.modelAnalysis]).slice(0, 9).length + 1) * 30 + 150 }}
                  />
                  <div className={s.disclosures}>
                    As of {expAsof}. The portfolio is subject to underlying risks of the Geography
                          exposures given. For a detailed discussion of these risks of the underlying Geography
                          exposures please see the <a href="https://www.wisdomtree.com/advisor-solutions/digital-portfolio-developer/important-information#important-risk-information" target="_blank">Important Risk Information</a>.
              </div>
                </div>
              </div>
            }
            {selectedMetric.value === 'marketcap' &&
              <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                <div className="col-md-12">
                  <ReactEcharts
                    option={this.getMarketCapChart(clientModel, mod, client.firstName)}
                    theme='default'
                    style={{ height: 300 }}
                  />
                  <div className={s.disclosures}>
                    As of {expAsof}. The portfolio is subject to underlying risks of the Market
                          Cap exposures given. For a detailed discussion of these risks of the underlying Market
                          Cap exposures please see the <a href="https://www.wisdomtree.com/advisor-solutions/digital-portfolio-developer/important-information#important-risk-information" target="_blank">Important Risk Information</a>.
                        </div>
                </div>
              </div>
            }
            {selectedMetric.value === 'stdperf' &&
              <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                <div className="col-md-12" style={{paddingLeft:'0px',paddingRight:'0px'}}>
                  <StandardPerformanceTable
                    data={[mod.portfolioAnalysis, mod.modelAnalysis]}
                    showTitle={false}
                    showCommonPeriod={false}
                    isPortfolio={true}
                    showSinceInception={true}
                    showPerfFootnote={false}
                    hideCumulativeReturns={false}
                    wtFundsOnly={false}
                    sidePadding={0}
                  />
                </div>
                <div className="col-md-12">
                  <div className={s.disclosures}>
                    <span style={{ fontWeight: '900' }}>Performance is historical
                    and does not guarantee future results. Current performance may be lower or higher than quoted.
                    Investment returns and principal value of an investment will fluctuate so that an investor's
                          shares, when sold, may be worth more or less than their original cost.</span><br />
                          <a style={{color:'#37BAC0',fontWeight:'700'}} onClick={() => this.selectMetric(stdperfportOption)}>
                            View Quarterly Standardized Performance for Client Portfolio Constituents
                          </a><br />
                          <a style={{color:'#5060F6',fontWeight:'700'}} onClick={() => this.selectMetric(stdperfmodelOption)}>
                            View Quarterly Standardized Performance for Wisdomtree Model Constituents
                          </a><br />
                          The Model Portfolio performance results shown are theoretical and do not reflect any
                          investor's actual experience with owning, trading or managing an actual investment.
                          Thus, the performance shown does not reflect the impact that economic and market factors
                          had or might have had on decision making if actual investor money had been managed and
                          allocated per the Model Portfolio. Actual performance achieved in seeking to follow
                          the Model Portfolio may differ from the theoretical performance shown for a number of
                          reasons, including the timing of implementation of trades (including rebalancing
                          trades to adjust to Model Portfolio changes), market conditions, fees and expenses
                          (e.g., brokerage commissions, deduction of advisory or other fees or expenses
                          charged by advisors or other third parties to investors, strategist fees and/or platform fees),
                          contributions, withdrawals, account restrictions, tax consequences, and/or other factors, any
                          or all of which may lower returns. While Model Portfolio performance may have performed
                          better than the benchmark for some or all periods shown, the performance during any other
                          period may not have, and there is no assurance that Model Portfolio performance will perform
                          better than the benchmark in the future. Model Portfolio performance calculations assume
                          reinvestment of dividends, are pre-tax and are net of fund expenses.<br />
                          ETF shares are bought and sold at market price (not NAV) and are not individually
                          redeemable from the Fund. Total returns are calculated using the daily 4:00 p.m. EST
                          net asset value (NAV). Market price returns reflect the midpoint of the bid/ask spread
                          as of the close of trading on the exchange where Fund shares are listed. Market price
                          returns do not represent the returns you would receive if you traded shares at other times.
                        </div>
                </div>
              </div>
            }
            {selectedMetric.value === 'stdperfport' &&
              <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                <div className="col-md-12" style={{paddingLeft:'0px',paddingRight:'0px'}}>
                  <StandardPerformanceTable
                    data={stdPerfPort}
                    showTitle={false}
                    showCommonPeriod={false}
                    isPortfolio={false}
                    showSinceInception={true}
                    showPerfFootnote={false}
                    hideCumulativeReturns={false}
                    wtFundsOnly={false}
                    sidePadding={0}
                  />
                </div>
                <div className="col-md-12">
                  <div className={s.disclosures}>
                    <span style={{ fontWeight: '900' }}>Performance is historical
                    and does not guarantee future results. Current performance may be lower or higher than quoted.
                    Investment returns and principal value of an investment will fluctuate so that an investor's
                          shares, when sold, may be worth more or less than their original cost.</span><br />
                          The Model Portfolio performance results shown are theoretical and do not reflect any
                          investor's actual experience with owning, trading or managing an actual investment.
                          Thus, the performance shown does not reflect the impact that economic and market factors
                          had or might have had on decision making if actual investor money had been managed and
                          allocated per the Model Portfolio. Actual performance achieved in seeking to follow
                          the Model Portfolio may differ from the theoretical performance shown for a number of
                          reasons, including the timing of implementation of trades (including rebalancing
                          trades to adjust to Model Portfolio changes), market conditions, fees and expenses
                          (e.g., brokerage commissions, deduction of advisory or other fees or expenses
                          charged by advisors or other third parties to investors, strategist fees and/or platform fees),
                          contributions, withdrawals, account restrictions, tax consequences, and/or other factors, any
                          or all of which may lower returns. While Model Portfolio performance may have performed
                          better than the benchmark for some or all periods shown, the performance during any other
                          period may not have, and there is no assurance that Model Portfolio performance will perform
                          better than the benchmark in the future. Model Portfolio performance calculations assume
                          reinvestment of dividends, are pre-tax and are net of fund expenses.<br />
                          ETF shares are bought and sold at market price (not NAV) and are not individually
                          redeemable from the Fund. Total returns are calculated using the daily 4:00 p.m. EST
                          net asset value (NAV). Market price returns reflect the midpoint of the bid/ask spread
                          as of the close of trading on the exchange where Fund shares are listed. Market price
                          returns do not represent the returns you would receive if you traded shares at other times.
                        </div>
                </div>
              </div>
            }
            {selectedMetric.value === 'stdperfmodel' &&
              <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                <div className="col-md-12" style={{paddingLeft:'0px',paddingRight:'0px'}}>
                  <StandardPerformanceTable
                    data={stdPerfModel}
                    showTitle={false}
                    showCommonPeriod={false}
                    isPortfolio={false}
                    showSinceInception={true}
                    showPerfFootnote={false}
                    hideCumulativeReturns={false}
                    wtFundsOnly={false}
                    sidePadding={0}
                  />
                </div>
                <div className="col-md-12">
                  <div className={s.disclosures}>
                    <span style={{ fontWeight: '900' }}>Performance is historical
                    and does not guarantee future results. Current performance may be lower or higher than quoted.
                    Investment returns and principal value of an investment will fluctuate so that an investor's
                          shares, when sold, may be worth more or less than their original cost.</span><br />
                          The Model Portfolio performance results shown are theoretical and do not reflect any
                          investor's actual experience with owning, trading or managing an actual investment.
                          Thus, the performance shown does not reflect the impact that economic and market factors
                          had or might have had on decision making if actual investor money had been managed and
                          allocated per the Model Portfolio. Actual performance achieved in seeking to follow
                          the Model Portfolio may differ from the theoretical performance shown for a number of
                          reasons, including the timing of implementation of trades (including rebalancing
                          trades to adjust to Model Portfolio changes), market conditions, fees and expenses
                          (e.g., brokerage commissions, deduction of advisory or other fees or expenses
                          charged by advisors or other third parties to investors, strategist fees and/or platform fees),
                          contributions, withdrawals, account restrictions, tax consequences, and/or other factors, any
                          or all of which may lower returns. While Model Portfolio performance may have performed
                          better than the benchmark for some or all periods shown, the performance during any other
                          period may not have, and there is no assurance that Model Portfolio performance will perform
                          better than the benchmark in the future. Model Portfolio performance calculations assume
                          reinvestment of dividends, are pre-tax and are net of fund expenses.<br />
                          ETF shares are bought and sold at market price (not NAV) and are not individually
                          redeemable from the Fund. Total returns are calculated using the daily 4:00 p.m. EST
                          net asset value (NAV). Market price returns reflect the midpoint of the bid/ask spread
                          as of the close of trading on the exchange where Fund shares are listed. Market price
                          returns do not represent the returns you would receive if you traded shares at other times.
                        </div>
                </div>
              </div>
            }
            {selectedMetric.value === 'portdetails' &&
              <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                <div className="col-md-12" style={{paddingLeft:'15px',paddingRight:'15px'}}>
                  <DetailsTable
                    data={portDetailsData}
                    showTitle={false}
                    showCommonPeriod={false}
                    isPortfolio={false}
                    showPerfFootnote={true}
                    sidePadding={0}
                  />
                </div>
                <div className="col-md-12">
                </div>
              </div>
            }
            {selectedMetric.value === 'modeldetails' &&
              <div className='row' style={{paddingLeft:'15px',paddingRight:'15px'}}>
                <div className="col-md-12" style={{paddingLeft:'15px',paddingRight:'15px'}}>
                  <DetailsTable
                    data={modelDetailsData}
                    showTitle={false}
                    showCommonPeriod={false}
                    isPortfolio={false}
                    showPerfFootnote={true}
                    sidePadding={0}
                  />
                </div>
                <div className="col-md-12">
                </div>
              </div>
            }
          </div>
          </div>
        );
  })
    )
}


render() {
  const {
    client,
  } = this.props;

  const {
    viewAllModels,
  } = this.state;

  if (client.holdings.length > 0) {
    const cleanHoldings = [{
      tickers: client.holdings[client.holdings.length - 1].tickers,
      weights: client.holdings[client.holdings.length - 1].weights,
    }];
    client.holdings = cleanHoldings;
  }

  return (
    <div>
      {!client.isDirty && client.fullTermModels.length > 0 &&
        <div className="row" style={{ marginTop: '0px' }}>
          <div className="col-md-12">
            {this.renderSelectedModels()}
          </div>
        </div>
      }
      {!client.isDirty && client.fullTermModels.length > 3 && viewAllModels === false &&
        <div className="row" style={{ marginTop: '60px' }}>
          <div className="col-md-12">
            <div
              onClick={this.setViewAllModels}
              className={s.showMore}
            >VIEW MORE MODELS</div>
          </div>
        </div>
      }
    </div>
  );
}

getMonteCarloChart(model, maxMonteCarloValue, goalMonteCarlo, color) {
  return {
    grid: {
      left: '0%',
      right: '18%',
      bottom: '3%',
      top: '3%',
      containLabel: true
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name + ' Years';
        for (let i = 0, l = params.length; i < l; i++) {
          if (!params[i].value || i != 4) {
            continue;
          }
          res += `<br/>${params[i].seriesName} : $${Math.round(params[i].value / 1000)}k`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#05A9B1',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    xAxis: {
      type: 'category',
      axisLabel: {
        show: true,
        fontSize: 12,
        align: 'right',
        color: '#8C959B',
        formatter: function (val) {
          if (val === '' + (model.returnsHigh.length - 1)) {
            return (model.returnsHigh.length - 1) + ' Years';
          } else {
            return '';
          }
        }
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: '#8C959B',
        }
      },
      axisTick: {
        show: false
      },
      data: model.returnsHigh.map((high, idx) => {
        return idx;
      }),
      splitLine: {
        show: false
      },
      boundaryGap: false
    },
    yAxis: {
      max: maxMonteCarloValue,
      axisLabel: {
        fontSize: 14,
        color: '#8C959B',
        formatter: function (val) {
          return '$' + (val / 1000) + 'k';
        }
      },
      axisTick: {
        show: false
      },
      axisLine: {
        lineStyle: {
          color: '#8C959B',
        }
      },
      splitNumber: 2,
      splitLine: {
        show: false
      }
    },
    series: [{
      name: 'Goal',
      type: 'line',
      smooth: true,
      data: model.returnsLow.map((value) => {
        return goalMonteCarlo;
      }),
      lineStyle: {
        opacity: 0,
        color: 'red'
      },
      areaStyle: {
        color: '#EBEBEB',
      },
      symbol: 'none'
    }, {
      name: 'Low',
      type: 'line',
      smooth: true,
      data: model.returnsLow.map((value) => {
        return value * .04;
      }),
      lineStyle: {
        opacity: 0,
        color: 'red'
      },
      stack: 'confidence-band',
      symbol: 'none'
    }, {
      name: 'Mid',
      type: 'line',
      smooth: true,
      data: model.returnsMedian.map((value, idx) => {
        return (value - model.returnsLow[idx]) * .04;
      }),
      lineStyle: {
        opacity: 0,
        color: 'green'
      },
      areaStyle: {
        color: color
      },
      stack: 'confidence-band',
      symbol: 'none'
    }, {
      name: 'High',
      type: 'line',
      smooth: true,
      data: model.returnsHigh.map((value, idx) => {
        return (value - model.returnsMedian[idx]) * .04;
      }),
      lineStyle: {
        opacity: 0,
        color: 'green'
      },
      areaStyle: {
        color: color
      },
      stack: 'confidence-band',
      symbol: 'none'
    }, {
      name: 'Median Yearly Income',
      type: 'line',
      smooth: true,
      data: model.returnsMedian.map((value) => {
        return value * .04;
      }),
      hoverAnimation: false,
      symbolSize: 2,
      lineStyle: {
        width: 2,
        color: '#FFF',
      },
      showSymbol: false,
      markLine: {
        silent: true,
        label: {
          fontSize: 14,
          color: '#8C959B',
          formatter: function (val) {
            return '$' + (val.value / 1000) + 'k Goal';
          }
        },
        lineStyle: {
          color: '#5b6770',
          type: 'dashed',
          width: 1,
        },
        symbol: ['none', 'none'],
        data: [{
          yAxis: goalMonteCarlo,
        }]
      }
    }]
  };
}

getSectorChart(clientModel, mod, clientName) {

  const data = [clientModel.portfolioAnalysis, mod.modelAnalysis];
  const sectors = data.map((fund) => {
    if (fund.sectorExposures) {
      return (
        fund.sectorExposures[fund.sectorExposures.length - 1].keys
      );
    } else {
      return null;
    }
  });

  const allSectors = [].concat(...sectors);
  const uniqueSectors = allSectors.filter((v, i, a) => a.indexOf(v) === i);
  const xaxisData = [...uniqueSectors].sort();
  // set series data
  const seriesData = [];
  for (let d = 0; d < data.length; d++) {
    const ticker = d === 0 ? clientName + "'s Portfolio" : "WisdomTree Model/Client Portfolio Blend";
    const sects = data[d].sectorExposures[data[d].sectorExposures.length - 1];
    const values = xaxisData.map((name, idx) => {
      const index = sects.keys.findIndex((x) => x === name);
      if (index !== -1) {
        return (parseFloat((sects.values[index] * 100).toFixed(2)));
      } else {
        return (0);
      }
    });
    seriesData.push({ name: ticker, barGap: 0, type: 'bar', data: values });
  }

  return {
    legend: {
      itemGap: 40,
      data: [clientName + "'s Portfolio", "WisdomTree Model/Client Portfolio Blend"],
      textStyle: {
        fontSize: 16,
        color: '#5b6770',
      },
      icon: 'circle',
      right: 0,
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name;
        for (let i = 0, l = params.length; i < l; i++) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#05A9B1',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      left: '10%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          fontSize: 14,
          color: '#8C959B',
          rotate: 30,
          interval: 0,
          align: 'right',
          formatter: function (param) {
            return param.replace('&amp;', '&');
          },
        },
        axisPointer: {
          type: 'shadow',
        },
        axisLine: {
          lineStyle: {
            color: '#8C959B',
          }
        },
        data: xaxisData,
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          fontSize: 14,
          color: '#8C959B',
          formatter: function (val) {
            return '' + (val) + '%';
          }
        },
        axisTick: {
          show: false
        },
        axisLine: {
          lineStyle: {
            color: '#8C959B',
          }
        },
        splitNumber: 2,
        splitLine: {
          show: false
        }
      },
    ],
    series: seriesData,
    color: ['#37BAC0', '#5060F6']
  };
}

getMarketCapChart(clientModel, mod, clientName) {
  const data = [clientModel.portfolioAnalysis, mod.modelAnalysis];
  const MarketCap = data.map((fund) => {
    if (fund.marketCapExposures) {
      return (
        fund.marketCapExposures[fund.marketCapExposures.length - 1].keys
      );
    } else {
      return null;
    }
  });

  const allMarketCap = [].concat(...MarketCap);
  const uniqueMarketCap = allMarketCap.filter((v, i, a) => a.indexOf(v) === i);
  const xaxisData = [...uniqueMarketCap].sort();
  const seriesData = [];
  let xaxisDataFormat = [];
  for (let d = 0; d < data.length; d++) {
    if (!data[d].marketCapExposures) {
      continue;
    }
    const mkts = data[d].marketCapExposures[data[d].marketCapExposures.length - 1];
    const ticker = d == 0 ? clientName + "'s Portfolio" : "WisdomTree Model/Client Portfolio Blend";
    const values = xaxisData.map((name) => {
      const index = mkts.keys.findIndex((x) => x === name);
      if (index !== -1) {
        return (parseFloat((mkts.values[index] * 100).toFixed(2)));
      } else {
        return (0);
      }
    });
    xaxisDataFormat = xaxisData.map((name) => {
      if (name === 'largeCap') {
        return ('Large Cap');
      } else if (name === 'midCap') {
        return ('Mid Cap');
      } else if (name === 'smallCap') {
        return ('Small Cap');
      } else {
        return name;
      }
    });
    seriesData.push({ name: ticker, barGap: 0, type: 'bar', data: values });
  }

  return {
    legend: {
      itemGap: 40,
      data: [clientName + "'s Portfolio", "WisdomTree Model/Client Portfolio Blend"],
      textStyle: {
        fontSize: 16,
        color: '#5b6770',
      },
      icon: 'circle',
      right: 0,
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name;
        for (let i = 0, l = params.length; i < l; i++) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#05A9B1',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          fontSize: 14,
          color: '#8C959B',
        },
        axisPointer: {
          type: 'shadow',
        },
        axisLine: {
          lineStyle: {
            color: '#8C959B',
          }
        },
        data: xaxisDataFormat,
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          fontSize: 14,
          color: '#8C959B',
          formatter: function (val) {
            return '' + (val) + '%';
          }
        },
        axisTick: {
          show: false
        },
        axisLine: {
          lineStyle: {
            color: '#8C959B',
          }
        },
        splitNumber: 2,
        splitLine: {
          show: false
        }
      },
    ],
    series: seriesData,
    color: ['#37BAC0', '#5060F6']
  };
}

getUniqueCountries(data) {
  const countries = data.map((fund) => {
    if (fund.countryExposures) {
      return (
        fund.countryExposures[fund.countryExposures.length - 1].keys
      );
    } else {
      return null;
    }
  });
  const allCountries = [].concat(...countries);

  const countriesValues = data.map((fund) => {
    if (fund.countryExposures) {
      return (
        fund.countryExposures[fund.countryExposures.length - 1].values
      );
    } else {
      return null;
    }
  });
  const allCountriesValues = [].concat(...countriesValues);
  const list = allCountries.map((country, i) => ({ country, countryV: allCountriesValues[i] }));
  list.sort((a, b) => a.countryV < b.countryV ? 1 : a.countryV === b.countryV ? a.country < b.country ? 1 : -1 : -1);
  const orderedCountries = [];
  list.forEach(function (arrayItem) {
    orderedCountries.push(arrayItem.country);
  });
  return orderedCountries.filter((v, i, a) => a.indexOf(v) === i);
}

getCountryChart(clientModel, mod, clientName) {
  const data = [clientModel.portfolioAnalysis, mod.modelAnalysis];
  const yaxisData = this.getUniqueCountries(data).slice(0, 9);
  yaxisData.push('Other');
  // set series data
  const seriesData = [];
  for (let d = 0; d < data.length; d++) {
    if (!data[d].countryExposures) {
      continue;
    }
    const countries = data[d].countryExposures[data[d].countryExposures.length - 1];
    const ticker = d == 0 ? clientName + "'s Portfolio" : "WisdomTree Model/Client Portfolio Blend";
    let total = 0;
    const values = yaxisData.map(name => {
      const index = countries.keys.findIndex(x => x === name);
      let value = 0;
      if (index !== -1) {
        value = parseFloat((countries.values[index] * 100).toFixed(2));
        total += value;
      } else if (name === 'Other') {
        value = parseFloat((100.0 - total).toFixed(2));
      }
      return value;
    });
    seriesData.push({ name: ticker, barGap: 0, type: 'bar', data: values });
  }

  return {
    legend: {
      itemGap: 40,
      data: [clientName + "'s Portfolio", "WisdomTree Model/Client Portfolio Blend"],
      textStyle: {
        fontSize: 16,
        color: '#5b6770',
      },
      icon: 'circle',
      right: 0,
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let resp = params[0].name;
        for (let i = 0, l = params.length; i < l; i++) {
          resp += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return resp;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#05A9B1',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left'
      }
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true
    },
    yAxis: [
      {
        type: 'category',
        inverse: true,
        axisLabel: {
          fontSize: 14,
          color: '#8C959B',
          interval: 0,
          align: 'right'
        },
        axisTick: {
          show: true
        },
        axisLine: {
          lineStyle: {
            color: '#8C959B',
          }
        },
        axisPointer: {
          type: 'shadow'
        },
        data: yaxisData
      }
    ],
    xAxis: [
      {
        type: 'value',
        axisLabel: {
          formatter: '{value}%',
          fontSize: 14,
          color: '#8C959B'
        },
        axisTick: {
          show: false
        },
        axisLine: {
          lineStyle: {
            color: '#8C959B',
          }
        },
        splitLine: {
          show: false
        }
      }
    ],
    series: seriesData,
    color: ['#37BAC0', '#5060F6']
  };
}

}

function mapStateToProps(state) {
  return {
    editMode: state.advisorData.editMode,
  };
}

export default connect(mapStateToProps, {
  setEditMode,
  deleteClient,
  setEditClient,
})(PreferredModels);



// WEBPACK FOOTER //
// ./src/components/AdvisorMode/PreferredModels.js